import { useState } from "react";

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { listOrganizations } from "@/api/organizations";
import { type BulkResult, bulkAddMembership } from "@/api/practitioners";
import type { MembershipKindInOrg } from "@/api/types";

const KINDS: MembershipKindInOrg[] = [
  "SALARIE",
  "LIBERAL",
  "VACATAIRE",
  "STAGIAIRE",
  "RESERVISTE",
];

type Props = {
  open: boolean;
  ids: string[];
  onClose: () => void;
  onDone: () => void;
};

export function BulkAddMembershipDialog({ open, ids, onClose, onDone }: Props) {
  const { t } = useTranslation();
  const [orgId, setOrgId] = useState<string>("");
  const [kind, setKind] = useState<MembershipKindInOrg>("SALARIE");
  const [result, setResult] = useState<BulkResult | null>(null);

  const orgs = useQuery({
    queryKey: ["organizations-all-active"],
    queryFn: () => listOrganizations({ status: "ACTIVE", limit: 200 }),
    enabled: open,
  });

  const mutation = useMutation({
    mutationFn: () =>
      bulkAddMembership({
        practitioner_ids: ids,
        organization_id: orgId,
        kind,
      }),
    onSuccess: (r) => {
      setResult(r);
      onDone();
    },
  });

  const close = () => {
    setResult(null);
    setOrgId("");
    setKind("SALARIE");
    mutation.reset();
    onClose();
  };

  const errorMsg =
    (mutation.error as { response?: { data?: { detail?: string } } })?.response?.data?.detail;

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>{t("practitionersBulk.addMembershipTitle")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {!result && (
            <>
              <Alert severity="info">
                {t("practitionersBulk.addMembershipHint", { count: ids.length })}
              </Alert>
              <FormControl fullWidth size="small">
                <InputLabel>{t("nav.organizations")}</InputLabel>
                <Select
                  label={t("nav.organizations")}
                  value={orgId}
                  onChange={(e) => setOrgId(e.target.value)}
                >
                  {(orgs.data?.items ?? []).map((o) => (
                    <MenuItem key={o.id} value={o.id}>
                      {o.code} — {o.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>{t("practitioners.kind")}</InputLabel>
                <Select
                  label={t("practitioners.kind")}
                  value={kind}
                  onChange={(e) => setKind(e.target.value as MembershipKindInOrg)}
                >
                  {KINDS.map((k) => (
                    <MenuItem key={k} value={k}>
                      {k}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
          {result && (
            <>
              <Alert severity="success">
                {t("practitionersBulk.addMembershipDone", {
                  processed: result.processed,
                  skipped: result.skipped,
                })}
              </Alert>
              <Box>
                <Stack direction="row" spacing={1}>
                  <Chip color="primary" label={`${t("practitionersBulk.requested")}: ${result.requested}`} />
                  <Chip color="success" label={`${t("practitionersBulk.processed")}: ${result.processed}`} />
                  <Chip color="default" label={`${t("practitionersBulk.skipped")}: ${result.skipped}`} />
                </Stack>
              </Box>
            </>
          )}
          {errorMsg && <Alert severity="error">{errorMsg}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={mutation.isPending}>
          {result ? t("common.close") : t("common.cancel")}
        </Button>
        {!result && (
          <Button
            variant="contained"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || !orgId || ids.length === 0}
            startIcon={mutation.isPending ? <CircularProgress size={16} /> : undefined}
          >
            {t("practitionersBulk.addMembershipCta")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
